import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { BUTTONS, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { TAG_TEXT } from "@/utils/constants/messages";
import { useAppDispatch } from "@/state/hooks";
import { editTagThunk } from "@/state/tags/thunks";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Button } from "@/components/ui/button";

const EditTagModal = (props: { show: boolean; onHide?: () => void; selectedTag?: { id?: string | number; name?: string } }) => {
    const dispatch = useAppDispatch();
    const [tagName, setTagName] = React.useState("");
    const [isLoading, setIsLoading] = React.useState(false);
    const [errMsg, setErrMsg] = React.useState("");

    React.useEffect(() => {
        if (props.show && props.selectedTag?.name) {
            setTagName(props.selectedTag.name);
        }
    }, [props.show, props.selectedTag]);

    const handleHideModal = () => {
        setTagName("");
        setErrMsg("");
        props.onHide?.();
    };

    const handleEditTag = async () => {
        if (tagName.trim() === "") {
            setErrMsg(TAG_TEXT.VALID_TAG_NAME);
            return;
        }
        if (!props.selectedTag?.id) {
            setErrMsg(TAG_TEXT.FAILED_EDIT_TAG);
            return;
        }
        setIsLoading(true);
        setErrMsg("");
        try {
            await dispatch(editTagThunk({ id: props.selectedTag.id, tagName })).unwrap();
            setIsLoading(false);
            handleHideModal();
        } catch (err: any) {
            setIsLoading(false);
            setErrMsg(err?.message || TAG_TEXT.FAILED_EDIT_TAG);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            handleEditTag();
        }
    };

    return (
        <Dialog open={props.show} onOpenChange={(open) => { if (!open) handleHideModal(); }}>
            <DialogContent className="gs-tag-dialog">
                <div className="gs-tag-dialog__header">
                    <h4 className="gs-tag-dialog__title">{HEADINGS.EDIT_TAG}</h4>
                </div>

                <div className="gs-tag-dialog__body">
                    <label className="gs-tag-dialog__label" htmlFor="update-tag-name-input" id="update-tag-name-label">
                        {LABELS.TAG_NAME}
                    </label>
                    <input
                        id="update-tag-name-input"
                        type="text"
                        autoFocus
                        value={tagName}
                        onKeyDown={handleKeyDown}
                        onChange={(e) => setTagName(e.target.value)}
                        className="gs-tag-dialog__input"
                    />
                    <p className="gs-tag-dialog__helper">{TAG_TEXT.EDIT_MESSAGE}</p>
                    {errMsg ? <p className="gs-tag-dialog__error">{errMsg}</p> : null}
                </div>

                <div className="gs-tag-dialog__footer">
                    <Button
                        type="button"
                        id="edit-tag-btn-modal"
                        className={loadingButtonClass(isLoading, "gs-tag-dialog__submit")}
                        onClick={handleEditTag}
                        disabled={isLoading}
                        aria-busy={isLoading || undefined}
                    >
                        <ButtonLoadingContent loading={isLoading} loaderId="edit-tag-btn-modal-loader">
                            {BUTTONS.UPDATE}
                        </ButtonLoadingContent>
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default EditTagModal;
